// Asset loading with placeholder fallback (architecture §9.6):
// - a slot listed in the manifest is fetched as `/assets/<path>?v=<hash>` and decoded with
//   `createImageBitmap`; otherwise, or on any error, its placeholder is used (one console.warn
//   per failure, never an error);
// - brand/UI first (the menu waits at most 1.5 s), world sprites next, skins lazily on first use;
// - `setManifest` (dev hot reload, M2) reloads slots whose hash changed and bumps `version`.
//
// Implementer: client render/assets. Consumers: render (world images, skin bitmaps), ui
// (wordmark, icons), app.ts (boot).
//
// Details:
// - A slot that is not in the manifest is simply "no art": its placeholder is used silently. Only
//   a listed file that fails (HTTP error, network, decode) is warned about, once per file version.
// - While a changed file reloads, the previous image stays in use; a failed reload falls back to
//   the placeholder.
// - Skins: preset ids come from the manifest; `c:<hash16>` from /skins/custom (PNG, then WebP);
//   `local:<uuid>` through `opts.localSkin` (IndexedDB, M2). When the manifest has no skins at
//   all, the twelve placeholder skins (art-assets §4.2) are drawn on demand.
// - `startHotReload()` polls the manifest every 3 s (dev); a failed poll keeps the current art.

import { hexToRgb, type Rgb } from '@ballio/shared';
import {
  type AssetManifest,
  manifestFileFor,
  type ManifestSkin,
  tryFetchManifest,
} from './manifest.js';
import { serverUrl } from '../net/server-url.js';
import { drawPlaceholderSkin, isPlaceholderSkinId } from './placeholders/skins.js';
import { IMAGE_SLOTS, type ImageSlot, slotById } from './slots.js';

export type AssetImage = ImageBitmap | HTMLCanvasElement | OffscreenCanvas | HTMLImageElement;

export interface AssetLoaderOptions {
  /**
   * Server base for /assets and /skins (net/server-url.ts); `""` = this page. Images are read
   * with fetch() + createImageBitmap, so a cross-origin server (with CORS) never taints the canvas.
   */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /** Warning sink (default console.warn). */
  readonly warn?: (msg: string) => void;
  /** Image decoder (default `createImageBitmap`, else an `<img>` element). */
  readonly decode?: (blob: Blob) => Promise<AssetImage>;
  /** Resolves `local:<uuid>` skins (IndexedDB, M2); without it they draw no skin. */
  readonly localSkin?: (uuid: string) => Promise<Blob | AssetImage | null>;
}

/** Custom skin ids from the server: `c:` and the first 16 hex digits of a SHA-256. */
const CUSTOM_SKIN_RE = /^c:([0-9a-f]{16})$/;
const LOCAL_SKIN_RE = /^local:([0-9a-zA-Z-]{1,64})$/;
/** Square skins smaller than this are refused (art-assets §4.1). */
const MIN_SKIN_PX = 128;

interface Entry {
  /** What was requested, e.g. `world/virus.png?v=9f2c1a7e` (changes when the file changes). */
  key: string;
  status: 'loading' | 'ready' | 'failed';
  /** The current image (kept while a newer version loads). */
  image: AssetImage | null;
  promise: Promise<void>;
}

function defaultWarn(msg: string): void {
  console.warn(msg);
}

function imageDims(img: AssetImage): { w: number; h: number } {
  if (typeof HTMLImageElement !== 'undefined' && img instanceof HTMLImageElement) {
    return { w: img.naturalWidth, h: img.naturalHeight };
  }
  return { w: (img as { width: number }).width, h: (img as { height: number }).height };
}

function isBlob(v: unknown): v is Blob {
  return typeof Blob !== 'undefined' && v instanceof Blob;
}

/** createImageBitmap, or an <img> from an object URL where that is missing. */
async function defaultDecode(blob: Blob): Promise<AssetImage> {
  if (typeof createImageBitmap === 'function') return createImageBitmap(blob);
  if (
    typeof document !== 'undefined' &&
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function'
  ) {
    const url = URL.createObjectURL(blob);
    try {
      const img = document.createElement('img');
      img.decoding = 'async';
      img.src = url;
      await img.decode();
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  throw new Error('no image decoder available');
}

export class AssetLoader {
  manifest: AssetManifest;
  /** Increments whenever any loaded image changes (renderer caches key on it). */
  version = 0;
  protected readonly opts: AssetLoaderOptions;
  private readonly slots = new Map<string, Entry>();
  private readonly skins = new Map<string, Entry>();
  private readonly placeholders = new Map<string, AssetImage | null>();
  private readonly dataUrls = new Map<string, string | null>();
  private readonly warned = new Set<string>();
  private readonly listeners = new Set<(version: number) => void>();
  private started = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private polling = false;
  private pollFailing = false;
  private fontsHooked = false;

  constructor(manifest: AssetManifest, opts: AssetLoaderOptions = {}) {
    this.manifest = manifest;
    this.opts = opts;
  }

  /** Loads brand + UI slots (resolves after at most `timeoutMs`), then starts world slots. */
  async loadInitial(timeoutMs = 1500): Promise<void> {
    this.started = true;
    this.hookFonts();
    const first: Promise<void>[] = [];
    for (const slot of IMAGE_SLOTS) {
      if (slot.group !== 'world' && this.listed(slot)) first.push(this.loadSlot(slot));
    }
    if (first.length > 0) {
      let timer: ReturnType<typeof setTimeout> | null = null;
      const timeout = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, Math.max(0, timeoutMs));
      });
      await Promise.race([Promise.all(first).then(() => undefined), timeout]);
      if (timer !== null) clearTimeout(timer);
    }
    for (const slot of IMAGE_SLOTS) {
      if (slot.group === 'world' && this.listed(slot)) void this.loadSlot(slot);
    }
  }

  /**
   * The user's art for a slot if loaded, else the slot's placeholder bitmap if it has one, else
   * null (the renderer then draws the procedural placeholder). Never throws, never blocks.
   */
  image(slotId: string): AssetImage | null {
    return this.userImage(slotId) ?? this.placeholder(slotId);
  }

  /**
   * Only the user's art for a slot (null while absent, loading or broken). Starts the load the
   * first time a listed slot is asked for.
   */
  userImage(slotId: string): AssetImage | null {
    const slot = slotById(slotId);
    if (!slot) return null;
    const entry = this.slots.get(slotId);
    if (entry) return entry.image;
    if (this.listed(slot)) void this.loadSlot(slot);
    return null;
  }

  /** The slot's runtime placeholder bitmap (generated once), or null. */
  placeholder(slotId: string): AssetImage | null {
    if (this.placeholders.has(slotId)) return this.placeholders.get(slotId) ?? null;
    const slot = slotById(slotId);
    let img: AssetImage | null = null;
    if (slot?.placeholder) {
      try {
        img = slot.placeholder(slot.width, slot.height);
      } catch (err) {
        this.warnOnce(
          `placeholder:${slotId}`,
          `ball.io: could not draw the placeholder for "${slotId}" (${String(err)})`,
        );
        img = null;
      }
    }
    this.placeholders.set(slotId, img);
    return img;
  }

  /** True when the slot's (or path's) multiply tint is on: the slot rule, unless world.json turns it off. */
  isTinted(slotId: string): boolean {
    const slot = slotById(slotId);
    if (!slot || !slot.tint) return false;
    return manifestFileFor(this.manifest, slot.path)?.file.tint !== false;
  }

  /** The URL of the user's file for a slot (for DOM `<img>`/CSS), or null when there is none. */
  url(slotId: string): string | null {
    const slot = slotById(slotId);
    if (!slot) return null;
    const found = manifestFileFor(this.manifest, slot.path);
    return found ? this.assetUrl(found.path, found.file.hash) : null;
  }

  /**
   * A URL for CSS (`mask-image`, `<img src>`, the favicon): the user's file, else a data URL of
   * the placeholder (when it is a DOM canvas), else null.
   */
  dataUrl(slotId: string): string | null {
    const direct = this.url(slotId);
    if (direct) return direct;
    if (this.dataUrls.has(slotId)) return this.dataUrls.get(slotId) ?? null;
    const img = this.placeholder(slotId);
    let url: string | null = null;
    if (img && typeof (img as HTMLCanvasElement).toDataURL === 'function') {
      try {
        url = (img as HTMLCanvasElement).toDataURL('image/png');
      } catch {
        url = null;
      }
    }
    this.dataUrls.set(slotId, url);
    return url;
  }

  /**
   * A skin bitmap: preset id (manifest), `c:<hash16>` (/skins/custom, M2) or `local:<uuid>`
   * (IndexedDB, M2). Starts a lazy load on first request and returns null until it is ready
   * (cells draw without the skin meanwhile).
   */
  skin(skinId: string): AssetImage | null {
    if (skinId === '') return null;
    const entry = this.skins.get(skinId);
    if (entry) return entry.image;
    this.loadSkin(skinId);
    return this.skins.get(skinId)?.image ?? null;
  }

  /** The outline colour for cells wearing a preset skin (manifest `border`), or null. */
  skinBorder(skinId: string): Rgb | null {
    const s = this.presetSkin(skinId);
    if (!s?.border) return null;
    try {
      return hexToRgb(s.border);
    } catch {
      return null;
    }
  }

  /** Swap in a newer manifest; changed slots reload. */
  setManifest(manifest: AssetManifest): void {
    if (manifest === this.manifest) return;
    const prev = this.manifest;
    this.manifest = manifest;
    this.dataUrls.clear();
    let changed =
      prev.theme !== manifest.theme &&
      JSON.stringify(prev.theme) !== JSON.stringify(manifest.theme);

    for (const [id, entry] of [...this.slots]) {
      const slot = slotById(id);
      const key = slot ? this.slotKey(slot) : null;
      if (key === entry.key) continue;
      if (!slot || key === null) {
        this.slots.delete(id);
        if (entry.image) changed = true;
      } else {
        void this.loadSlot(slot);
      }
    }
    if (this.started) {
      // New files appear in a running game (dev hot reload).
      for (const slot of IMAGE_SLOTS)
        if (!this.slots.has(slot.id) && this.listed(slot)) void this.loadSlot(slot);
    }

    for (const [id, entry] of [...this.skins]) {
      const key = this.skinKey(id);
      if (key === entry.key) continue;
      if (key === null) {
        this.skins.delete(id);
        if (entry.image) changed = true;
      } else {
        this.loadSkin(id, entry.image);
      }
    }
    if (changed) this.bump();
  }

  /** Calls `fn(version)` after every version bump. Returns an unsubscribe function. */
  onChange(fn: (version: number) => void): () => void {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  /**
   * Dev hot reload (architecture §9.6 step 5): polls the manifest every `intervalMs` and applies
   * changes. Returns a stop function (also `stopHotReload`).
   */
  startHotReload(intervalMs = 3000): () => void {
    if (this.pollTimer === null) {
      this.pollTimer = setInterval(() => void this.pollManifest(), Math.max(250, intervalMs));
    }
    return () => this.stopHotReload();
  }

  stopHotReload(): void {
    if (this.pollTimer !== null) clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  /**
   * Fetches the manifest once and applies it when it changed. Resolves true when a manifest was
   * read; a failure keeps the current art (warned once per failure streak).
   */
  async pollManifest(): Promise<boolean> {
    if (this.polling) return false;
    this.polling = true;
    try {
      const result = await tryFetchManifest({
        baseUrl: this.opts.baseUrl,
        fetchImpl: this.opts.fetchImpl,
      });
      if ('error' in result) {
        if (!this.pollFailing)
          this.warn(`ball.io: manifest poll failed (${result.error}); keeping the current art`);
        this.pollFailing = true;
        return false;
      }
      this.pollFailing = false;
      if (JSON.stringify(result.manifest) !== JSON.stringify(this.manifest))
        this.setManifest(result.manifest);
      return true;
    } finally {
      this.polling = false;
    }
  }

  /** Stops polling and drops listeners. */
  dispose(): void {
    this.stopHotReload();
    this.listeners.clear();
  }

  // --- internals --------------------------------------------------------------------------------

  private listed(slot: ImageSlot): boolean {
    return manifestFileFor(this.manifest, slot.path) !== null;
  }

  private assetUrl(path: string, hash: string): string {
    return serverUrl(this.opts.baseUrl ?? '', `/assets/${path}?v=${hash}`);
  }

  private slotKey(slot: ImageSlot): string | null {
    const found = manifestFileFor(this.manifest, slot.path);
    return found ? `${found.path}?v=${found.file.hash}` : null;
  }

  private loadSlot(slot: ImageSlot): Promise<void> {
    const found = manifestFileFor(this.manifest, slot.path);
    if (!found) return Promise.resolve();
    const key = `${found.path}?v=${found.file.hash}`;
    const existing = this.slots.get(slot.id);
    if (existing && existing.key === key) return existing.promise;
    const entry: Entry = {
      key,
      status: 'loading',
      image: existing?.image ?? null,
      promise: Promise.resolve(),
    };
    this.slots.set(slot.id, entry);
    entry.promise = this.fetchImage(this.assetUrl(found.path, found.file.hash)).then(
      (img) => {
        if (this.slots.get(slot.id) !== entry) return;
        entry.image = img;
        entry.status = 'ready';
        this.bump();
      },
      (err: unknown) => {
        if (this.slots.get(slot.id) !== entry) return;
        const had = entry.image !== null;
        entry.image = null;
        entry.status = 'failed';
        this.warnOnce(
          key,
          `ball.io: could not load art "${found.path}" (${reason(err)}); using the placeholder`,
        );
        if (had) this.bump();
      },
    );
    return entry.promise;
  }

  private presetSkin(skinId: string): ManifestSkin | undefined {
    return this.manifest.skins.find((s) => s.id === skinId);
  }

  /** What a skin id resolves to now (changes when its file changes); null = nothing to load. */
  private skinKey(skinId: string): string | null {
    const preset = this.presetSkin(skinId);
    if (preset) return `${preset.file}?v=${preset.hash}`;
    if (CUSTOM_SKIN_RE.test(skinId)) return skinId;
    if (LOCAL_SKIN_RE.test(skinId)) return this.opts.localSkin ? skinId : null;
    if (this.manifest.skins.length === 0 && isPlaceholderSkinId(skinId))
      return `placeholder:${skinId}`;
    return null;
  }

  private loadSkin(skinId: string, keep: AssetImage | null = null): void {
    const key = this.skinKey(skinId);
    const entry: Entry = {
      key: key ?? `none:${skinId}`,
      status: 'loading',
      image: keep,
      promise: Promise.resolve(),
    };
    this.skins.set(skinId, entry);
    if (key === null) {
      // Unknown here (e.g. a preset this server's manifest lacks): draw no skin, no warning.
      entry.status = 'failed';
      return;
    }
    const done = (img: AssetImage | null): void => {
      if (this.skins.get(skinId) !== entry) return;
      entry.image = img;
      entry.status = img ? 'ready' : 'failed';
      if (img || keep) this.bump();
    };
    const fail = (err: unknown, label: string): void => {
      if (this.skins.get(skinId) !== entry) return;
      this.warnOnce(
        `skin:${key}`,
        `ball.io: could not load skin "${label}" (${reason(err)}); drawing the cell without it`,
      );
      done(null);
    };

    const preset = this.presetSkin(skinId);
    const custom = CUSTOM_SKIN_RE.exec(skinId);
    const local = LOCAL_SKIN_RE.exec(skinId);
    if (preset) {
      entry.promise = this.fetchImage(this.assetUrl(preset.file, preset.hash))
        .then((img) => this.checkSkin(img))
        .then(done, (err: unknown) => fail(err, preset.file));
    } else if (custom) {
      const base = serverUrl(this.opts.baseUrl ?? '', `/skins/custom/${custom[1] ?? ''}`);
      entry.promise = this.fetchImage(`${base}.png`)
        .catch(() => this.fetchImage(`${base}.webp`))
        .then((img) => this.checkSkin(img))
        .then(done, (err: unknown) => fail(err, skinId));
    } else if (local && this.opts.localSkin) {
      const resolve = this.opts.localSkin;
      entry.promise = resolve(local[1] ?? '')
        .then((v) => (v === null ? null : isBlob(v) ? this.decode(v) : v))
        .then(done, (err: unknown) => fail(err, skinId));
    } else if (isPlaceholderSkinId(skinId)) {
      // Drawn after the current frame (a microtask), so first sight never stalls a frame.
      entry.promise = Promise.resolve().then(() => {
        let img: AssetImage | null;
        try {
          img = drawPlaceholderSkin(skinId);
        } catch {
          img = null;
        }
        done(img);
      });
    }
  }

  private checkSkin(img: AssetImage): AssetImage {
    const { w, h } = imageDims(img);
    if (w !== h) throw new Error(`skins must be square, got ${w}×${h}`);
    if (w < MIN_SKIN_PX) throw new Error(`skins must be at least ${MIN_SKIN_PX} px, got ${w}`);
    return img;
  }

  private async fetchImage(url: string): Promise<AssetImage> {
    const fetchImpl = this.opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
    if (!fetchImpl) throw new Error('fetch is unavailable');
    const res = await fetchImpl(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return this.decode(blob);
  }

  private decode(blob: Blob): Promise<AssetImage> {
    return (this.opts.decode ?? defaultDecode)(blob);
  }

  private bump(): void {
    this.version++;
    for (const fn of [...this.listeners]) {
      try {
        fn(this.version);
      } catch {
        // A listener's failure must not break loading.
      }
    }
  }

  private warnOnce(key: string, msg: string): void {
    if (this.warned.has(key)) return;
    this.warned.add(key);
    this.warn(msg);
  }

  private warn(msg: string): void {
    (this.opts.warn ?? defaultWarn)(msg);
  }

  /** Placeholders with text were maybe drawn in a fallback font: redraw them once fonts load. */
  private hookFonts(): void {
    if (this.fontsHooked) return;
    this.fontsHooked = true;
    const fonts = typeof document !== 'undefined' ? (document as Document).fonts : undefined;
    if (!fonts) return;
    try {
      fonts.addEventListener('loadingdone', () => {
        if (this.placeholders.size === 0) return;
        this.placeholders.clear();
        this.dataUrls.clear();
        this.bump();
      });
    } catch {
      // No font loading API.
    }
  }
}

function reason(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
